import 'react-datepicker/dist/react-datepicker.css';

import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import greaseTrapIcon from '../../../assets/icons/grease-trap.svg';
import saveBigIcon from '../../../assets/icons/save.svg';
import { useModal } from '../../../hooks/useModal';
import { createModalStateAtom } from '../../../recoil/modalConfirm';
import { successModalStateAtom } from '../../../recoil/modalSuccess';
import { needRefreshState } from '../../../recoil/needRefresh';
import { userStateAtom } from '../../../recoil/userData';
import { CREATE_GREASE_TRAP } from '../../../services/graphql/vendors/grease-traps/greaseTrapMutation';
import { GET_GREASE_TRAP_OPTIONS } from '../../../services/graphql/vendors/grease-traps/greaseTrapQuery';
import type { GetGreaseTrapOptionsResponse } from '../../../types/greaseTrap';
import { formatNumberMoney } from '../../../utils/numberFormatter';
import Breadcrumb from '../../ui/common/Breadcrumb';
import { Button } from '../../ui/common/Button';
import Dropdown from '../../ui/common/Inputs/Dropdown';
import InputNumber from '../../ui/common/Inputs/InputNumber';
import Toggle from '../../ui/common/Toggle';

const PRICE_PER_GALLON = 0.25;

type Inputs = {
  vendor_id?: string;
  driver_id?: string;
  truck_id?: string;
  location_id?: string;
  gross_pounds?: number | null;
  container_volume?: number | null;
  gallons_grease_received?: number | null;
  traceability?: boolean;
  registered_by?: string;
};

export const NewGreaseTrap = () => {
  const { t } = useTranslation('common');
  const user = useRecoilValue(userStateAtom);
  const setCreateModal = useSetRecoilState(createModalStateAtom);
  const setSuccessModal = useSetRecoilState(successModalStateAtom);
  const setNeedRefresh = useSetRecoilState(needRefreshState);
  const { handleCloseAllModals } = useModal();

  const {
    setValue,
    watch,
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      traceability: false,
    },
  });

  const { vendor_id: vendorId, gross_pounds: grossPounds } = watch();

  useEffect(() => {
    if (register) {
      register('vendor_id', { required: t('global.required') });
      register('location_id', { required: t('global.required') });
      register('driver_id', { required: t('global.required') });
      register('truck_id', { required: t('global.required') });
    }
  }, [register, t]);

  useEffect(() => {
    setValue('driver_id', '');
    setValue('truck_id', '');
    setValue('container_volume', null);
  }, [setValue, vendorId]);

  // Auto-calculate gallons from gross pounds: lbs / 8.1 = gal
  useEffect(() => {
    const gallons = grossPounds
      ? Math.round((Number(grossPounds) / 8.1) * 100) / 100
      : null;
    setValue('gallons_grease_received', gallons);
  }, [grossPounds, setValue]);

  const { data: options } = useQuery<GetGreaseTrapOptionsResponse>(
    GET_GREASE_TRAP_OPTIONS,
    {
      fetchPolicy: 'network-only',
      variables: { vendorId },
    },
  );

  const [createGreaseTrap, { loading }] = useMutation(CREATE_GREASE_TRAP);

  const refreshOrder = () => {
    setNeedRefresh('grease-traps');
  };

  const endRefreshOrder = () => {
    setTimeout(() => {
      setNeedRefresh(false);
    }, 500);
  };

  const closeOrder = () => {
    handleCloseAllModals();
    refreshOrder();
    endRefreshOrder();
  };

  const handleSave = (data: Inputs) => {
    setCreateModal({
      isOpen: true,
      message: t('modalNewGreaseTrap.confirmation-modal-text'),
      onCancel: () => {},
      onConfirm: () => {
        createGreaseTrap({
          variables: {
            ...data,
            registered_by: user.id,
            gross_pounds: Number(data.gross_pounds || 0),
            gallons_grease_received: Number(data.gallons_grease_received || 0),
            traceability: !!data.traceability,
          },
          onCompleted: () => {
            setCreateModal((prev) => ({ ...prev, isOpen: false }));
            setSuccessModal({
              message: t('modalNewGreaseTrap.success-modal-text'),
              title: t('modalNewGreaseTrap.success-modal-title'),
              isOpen: true,
            });
            closeOrder();
          },
        });
      },
      title: t('modalNewGreaseTrap.confirmation-modal-title'),
    });
  };

  const onTruckSelect = (value: string) => {
    setValue('truck_id', value);
    const truck = options?.getGreaseTrapOptions.truckOptions.find(
      (currentTruck) => currentTruck.value === value,
    );
    setValue('container_volume', truck?.capacity || null);
  };

  const totalAmount =
    Number(watch('gallons_grease_received') || 0) * PRICE_PER_GALLON;

  return (
    <div>
      <Breadcrumb
        title={t('modalNewGreaseTrap.breadcrumps-1')}
        secondTitle={t('modalNewGreaseTrap.breadcrumps-2')}
        image={greaseTrapIcon}
      />
      <h1 className="text-xl font-[600]">{t('modalNewGreaseTrap.title')}</h1>
      <form
        onSubmit={handleSubmit(handleSave)}
        className="my-6 flex flex-col gap-2"
      >
        <div className="h-[60px]">
          <Dropdown
            label={t('modalNewGreaseTrap.vendor')}
            selectedValue={watch('vendor_id') || undefined}
            errors={errors.vendor_id}
            options={options?.getGreaseTrapOptions.vendorOptions || []}
            onSelectChange={(value) => setValue('vendor_id', value)}
            isSearchable
          />
        </div>
        <div className="h-[60px]">
          <Dropdown
            label={t('modalNewGreaseTrap.driver')}
            selectedValue={watch('driver_id') || undefined}
            errors={errors.driver_id}
            options={
              vendorId ? options?.getGreaseTrapOptions.driverOptions || [] : []
            }
            onSelectChange={(value) => setValue('driver_id', value)}
          />
        </div>
        <div className="h-[60px]">
          <Dropdown
            label={t('modalNewGreaseTrap.truck')}
            selectedValue={watch('truck_id') || undefined}
            errors={errors.truck_id}
            options={
              vendorId ? options?.getGreaseTrapOptions.truckOptions || [] : []
            }
            onSelectChange={onTruckSelect}
          />
        </div>
        <div className="h-[60px]">
          <Dropdown
            label={t('modalNewGreaseTrap.location')}
            selectedValue={watch('location_id') || undefined}
            errors={errors.location_id}
            options={options?.getGreaseTrapOptions.locationOptions || []}
            onSelectChange={(value) => setValue('location_id', value)}
          />
        </div>
        <InputNumber
          control={control}
          label={t('modalNewGreaseTrap.gross-pounds')}
          suffix=" Lb"
          defaultValue=""
          registerName="gross_pounds"
          errors={errors.gross_pounds}
          validations={{
            required: t('global.required'),
          }}
          decimalCount={2}
          labelClassname="!font-[600]"
        />
        <InputNumber
          control={control}
          label={t('modalNewGreaseTrap.gallons-grease-received')}
          suffix=" Gal"
          disabled
          defaultValue=""
          registerName="gallons_grease_received"
          decimalCount={2}
          labelClassname="!font-[600]"
        />
        <InputNumber
          control={control}
          label={t('modalNewGreaseTrap.container-volume')}
          suffix=" Gal"
          disabled
          defaultValue=""
          registerName="container_volume"
          decimalCount={2}
          labelClassname="!font-[600]"
        />
        <div className="flex h-auto w-full flex-col items-start justify-center self-stretch rounded">
          <div className="my-2 flex flex-col items-start justify-center self-stretch text-[14px] font-[600]">
            <label className="text-sm font-normal">
              {t('modalNewGreaseTrap.total-amount')}
            </label>
          </div>
          <div className="flex w-full border-x-0 border-b-2 border-t-0 border-b-graydark pb-1 text-xs">
            {formatNumberMoney(totalAmount)}
          </div>
        </div>
        <div className="h-[60px]">
          <Toggle
            label={t('modalNewGreaseTrap.traceability')}
            selectedValue={watch('traceability') || false}
            onSelectChange={(value) => setValue('traceability', value)}
            className="flex flex-row items-center justify-between"
          />
        </div>
        <div className="mt-2 flex justify-end">
          <Button
            text={t('modalNewGreaseTrap.save')}
            variant="green"
            icon={saveBigIcon}
            type="submit"
            id="save-grease-trap"
            loading={loading}
            disabled={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default NewGreaseTrap;
